import { LoadPanel } from "devextreme-react";
import Button, { IButtonOptions } from "devextreme-react/button";
import { FormRef } from "devextreme-react/cjs/form";
import { FileUploader, FileUploaderRef } from "devextreme-react/file-uploader";
import { ButtonItem, Form, SimpleItem } from "devextreme-react/form";
import { HtmlEditorTypes } from "devextreme-react/html-editor";
import { Popup } from "devextreme-react/popup";
import { TagBoxTypes } from "devextreme-react/tag-box";
import notify from "../../lib/notify";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createAnnouncement, generateRecipients } from "../../api/announcement";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { RoleEnum } from "../../types";

type AnnouncementCreatePopupProps = {
  onClose: (announcementCreated: boolean) => void;
};

export default function AnnouncementCreatePopup({
  onClose,
}: AnnouncementCreatePopupProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();

  const htmlFormRef = useRef<HTMLFormElement>(null);
  const devExtremeFormRef = useRef<FormRef | null>(null);
  const fileUploaderRef = useRef<FileUploaderRef | null>(null);

  const formData = useRef({
    title: "",
    content: "",
    recipients: [] as string[],
    files: [] as File[],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [recipients, setRecipients] = useState<
    { label: string; value: string }[] | undefined
  >();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      devExtremeFormRef.current?.instance().option("disabled", true);

      createAnnouncement({
        title: formData.current.title,
        content: formData.current.content,
        recipients: formData.current.recipients,
        files: selectedFiles, // używamy lokalnej listy plików
      })
        .then(() => onClose(true))
        .catch((error) => {
          if (error instanceof Error) {
            notify(error.message, "error");
          } else {
            notify(t("announcements.create.messages.error"), "error");
          }
        })
        .finally(() => {
          devExtremeFormRef.current?.instance().option("disabled", false);
        });
    },
    [t, onClose, selectedFiles],
  );

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    generateRecipients(
      user,
      !user?.roles?.includes(RoleEnum.AdministratorGlowny) || companyId === 0
        ? undefined
        : companyId,
    )
      .then(setRecipients)
      .catch((error) => {
        setError(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
        );
      })
      .finally(() => setLoading(false));
  }, [user, companyId, t]);

  if (loading) {
    return <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />;
  }

  return (
    <Popup
      visible
      title={t("announcements.create.header")}
      onHiding={() => onClose(false)}
      maxWidth={700}
      height="auto"
      maxHeight="80vh"
      showCloseButton
      dragEnabled
    >
      {error ? (
        <div>{error}</div>
      ) : recipients === undefined ? (
        <div>{t("common.incorrectData")}</div>
      ) : (
        <form
          ref={htmlFormRef}
          encType="multipart/form-data"
          onSubmit={handleFormSubmit}
        >
          <Form ref={devExtremeFormRef} formData={formData.current}>
            <SimpleItem
              dataField="title"
              label={{
                text: t("announcements.create.inputs.title.label"),
              }}
              editorType="dxTextBox"
              isRequired
            />
            <SimpleItem
              dataField="content"
              label={{
                text: t("announcements.create.inputs.content.label"),
              }}
              editorType="dxHtmlEditor"
              editorOptions={
                {
                  height: 290,
                  toolbar: {
                    items: [
                      "bold",
                      "italic",
                      "underline",
                      "strike",
                      "separator",
                      "alignLeft",
                      "alignCenter",
                      "alignRight",
                      "alignJustify",
                      "separator",
                      "orderedList",
                      "bulletList",
                      "separator",
                      "link",
                      "clear",
                      "undo",
                      "redo",
                    ],
                  },
                } as HtmlEditorTypes.Properties
              }
              isRequired
            />
            <SimpleItem
              dataField="recipients"
              label={{
                text: t("announcements.create.inputs.recipients.label"),
              }}
              editorType="dxTagBox"
              editorOptions={
                {
                  dataSource: recipients,
                  valueExpr: "value",
                  displayExpr: "label",
                  searchEnabled: true,
                } as TagBoxTypes.Properties
              }
            />
            <SimpleItem
              dataField="files"
              label={{ text: t("announcements.create.inputs.file.jpgpngpdf") }}
            >
              <FileUploader
                ref={fileUploaderRef}
                uploadMode="useForm"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.gif"
                allowedFileExtensions={[
                  ".jpg",
                  ".jpeg",
                  ".png",
                  ".pdf",
                  ".gif",
                ]}
                onValueChanged={(e) => {
                  const files = e.value || [];
                  setSelectedFiles(files);
                }}
              />
              {/* ✅ Lista załączonych plików z opcją usunięcia */}
              {selectedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-gray-100 p-2 rounded-md"
                    >
                      <span className="text-sm truncate w-4/5">
                        {file.name}
                      </span>
                      <Button
                        icon="trash"
                        stylingMode="text"
                        hint={t("common.remove")}
                        onClick={() =>
                          setSelectedFiles((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </SimpleItem>
            <ButtonItem
              buttonOptions={
                {
                  useSubmitBehavior: true,
                  text: t("announcements.create.inputs.submit.label"),
                  stylingMode: "contained",
                  type: "default",
                } as IButtonOptions
              }
            />
          </Form>
        </form>
      )}
    </Popup>
  );
}
